import type {
  TickerBar,
  TickerBarBarStyle,
  TickerBarDividerMode,
  TickerBarMessage,
  TickerBarMessageInput,
  TickerBarStyle
} from 'types/api'
import {
  TICKER_BAR_BAR_DEFAULTS,
  TICKER_BAR_DIVIDER_MODES,
  TICKER_BAR_SHARED_DEFAULTS,
  TICKER_BAR_STAFF_FONT_KEY,
  TickerBarMarqueeInput,
  resolveTickerBarStyle
} from 'components/TickerBarMarquee'

export {
  TICKER_BAR_BAR_DEFAULTS,
  TICKER_BAR_DIVIDER_MODES,
  TICKER_BAR_SHARED_DEFAULTS,
  TICKER_BAR_STAFF_FONT_KEY
}

export const MAX_TICKER_BAR_NAME = 80
export const MAX_TICKER_BAR_MESSAGES = 100
export const MAX_TICKER_BAR_LABEL = 60
export const MAX_TICKER_BAR_TEXT = 300
export const MAX_TICKER_BAR_LOGO_URL = 1024
export const TICKER_BAR_PUSH_BUDGET_BYTES = 184320
export const TICKER_BAR_NEAR_BUDGET_FRACTION = 0.9
export const MAX_TICKER_BARS_PER_STREAM = 5
export const TICKER_BAR_FONT_KEY = 'INTER'

export const defaultFontKeyFor = (isStaff: boolean): string =>
  isStaff ? TICKER_BAR_STAFF_FONT_KEY : TICKER_BAR_FONT_KEY

export const isTickerBarDividerMode = (v: unknown): v is TickerBarDividerMode =>
  typeof v === 'string' && (TICKER_BAR_DIVIDER_MODES as readonly string[]).includes(v)

export interface TickerBarNumberBound {
  min: number
  max: number
  decimals: 0 | 2
}

export type TickerBarSharedNumberKey = 'scrollSpeedPxS' | 'borderWidth' | 'gapHeight' | 'textGlowPx'
export type TickerBarBarNumberKey =
  | 'opacity' | 'padTop' | 'padBottom' | 'outerPad' | 'lineHeightPx' | 'separatorGapPx' | 'labelGapPx'
  | 'labelFontWeight' | 'labelSizePx' | 'textFontWeight' | 'textSizePx' | 'logoHeightPx' | 'logoGapPx' | 'logoEdgePadPx'

export const TICKER_BAR_SHARED_BOUNDS: Readonly<Record<TickerBarSharedNumberKey, TickerBarNumberBound>> = {
  scrollSpeedPxS: { min: 40, max: 600, decimals: 0 },
  borderWidth: { min: 0, max: 20, decimals: 0 },
  gapHeight: { min: 0, max: 200, decimals: 0 },
  textGlowPx: { min: 0, max: 40, decimals: 0 }
}

export const TICKER_BAR_BAR_BOUNDS: Readonly<Record<TickerBarBarNumberKey, TickerBarNumberBound>> = {
  opacity: { min: 0, max: 1, decimals: 2 },
  padTop: { min: 0, max: 200, decimals: 2 },
  padBottom: { min: 0, max: 200, decimals: 2 },
  outerPad: { min: 0, max: 200, decimals: 2 },
  lineHeightPx: { min: 8, max: 200, decimals: 0 },
  separatorGapPx: { min: 0, max: 200, decimals: 2 },
  labelGapPx: { min: 0, max: 200, decimals: 0 },
  labelFontWeight: { min: 100, max: 1000, decimals: 0 },
  labelSizePx: { min: 8, max: 120, decimals: 0 },
  textFontWeight: { min: 100, max: 1000, decimals: 0 },
  textSizePx: { min: 8, max: 120, decimals: 0 },
  logoHeightPx: { min: 8, max: 200, decimals: 0 },
  logoGapPx: { min: 0, max: 200, decimals: 0 },
  logoEdgePadPx: { min: 0, max: 200, decimals: 0 }
}

export const clampToBound = (value: number, bound: TickerBarNumberBound, fallback: number): number => {
  if (!Number.isFinite(value)) return fallback
  const factor = bound.decimals === 2 ? 100 : 1
  const rounded = Math.round(value * factor) / factor
  return Math.min(bound.max, Math.max(bound.min, rounded))
}

const HEX6_RE = /^#[0-9a-fA-F]{6}$/
export const isHex6Color = (v: unknown): v is string => typeof v === 'string' && HEX6_RE.test(v)

export const TICKER_BAR_LABEL_COLOR_PICKS: ReadonlyArray<{ label: string, color: string }> = [
  { label: 'Green', color: '#3ddc5a' },
  { label: 'Red', color: '#ff4b47' },
  { label: 'White', color: '#ffffff' }
]

const REFUSED_CHARS_RE = /[\p{Cc}\u2028\u2029\u202A-\u202E\u2066-\u2069]/u
const LONE_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/

export const hasOnlyPrintableCharacters = (value: string): boolean =>
  !REFUSED_CHARS_RE.test(value) && !LONE_SURROGATE_RE.test(value)

export const unitsLeft = (value: string, cap: number): number => cap - value.length

export const clipToUnits = (value: string, cap: number): string => {
  if (value.length <= cap) return value
  const code = value.charCodeAt(cap - 1)
  const end = code >= 0xd800 && code <= 0xdbff ? cap - 1 : cap
  return value.slice(0, Math.max(0, end))
}

export const ESCAPED_UNIT_BYTES = 7

export const pushBytesOf = (value: unknown): number => {
  const wire = JSON.stringify(JSON.stringify(value))
  let bytes = 0
  for (let i = 0; i < wire.length; i++) bytes += wire.charCodeAt(i) < 0x80 ? 1 : ESCAPED_UNIT_BYTES
  return bytes
}

export const withinPushBudget = ({ topMessages, bottomMessages }: { topMessages: TickerBarMessage[], bottomMessages: TickerBarMessage[] }): boolean =>
  pushBytesOf({ topMessages, bottomMessages }) <= TICKER_BAR_PUSH_BUDGET_BYTES

export interface TickerBarMessageDraft {
  key: string
  label: string
  labelColor: string | null
  text: string
}

export interface TickerBarDraft {
  name: string
  topMessages: TickerBarMessageDraft[]
  bottomMessages: TickerBarMessageDraft[]
  style: TickerBarStyle
}

let localMessageSeq = 0
export const freshTickerBarMessageKey = (): string => `tb-local-${++localMessageSeq}`

export const emptyTickerBarMessage = (): TickerBarMessageDraft => ({
  key: freshTickerBarMessageKey(),
  label: '',
  labelColor: null,
  text: ''
})

export const newTickerBarStyle = (isStaff: boolean): TickerBarStyle => {
  const fontKey = defaultFontKeyFor(isStaff)
  const bar = (): TickerBarBarStyle => ({ ...TICKER_BAR_BAR_DEFAULTS, labelFontKey: fontKey, textFontKey: fontKey })
  return { ...TICKER_BAR_SHARED_DEFAULTS, top: bar(), bottom: bar() }
}

export const newTickerBarDraft = (isStaff: boolean): TickerBarDraft => ({
  name: '',
  topMessages: [],
  bottomMessages: [],
  style: newTickerBarStyle(isStaff)
})

const draftMessagesOf = (list: TickerBarMessage[]): TickerBarMessageDraft[] =>
  list.map((m) => ({
    key: freshTickerBarMessageKey(),
    label: m.label,
    labelColor: m.labelColor == null || m.labelColor === '' ? null : m.labelColor,
    text: m.text
  }))

export const tickerBarDraftOf = (row: TickerBar): TickerBarDraft => ({
  name: row.name,
  topMessages: draftMessagesOf(row.topMessages ?? []),
  bottomMessages: draftMessagesOf(row.bottomMessages ?? []),
  style: resolveTickerBarStyle(row.style)
})

export const normalizedTickerBarMessagesOf = (list: TickerBarMessageDraft[], prefix: 't' | 'b'): TickerBarMessage[] =>
  list.map((m, i) => ({
    id: `${prefix}${i}`,
    label: m.label.trim(),
    labelColor: m.labelColor == null || m.labelColor === '' ? null : m.labelColor,
    text: m.text.trim()
  }))

export interface TickerBarSaveInput {
  name: string
  topMessages: TickerBarMessageInput[]
  bottomMessages: TickerBarMessageInput[]
  style: TickerBarStyle
}

const inputMessagesOf = (list: TickerBarMessageDraft[]): TickerBarMessageInput[] =>
  normalizedTickerBarMessagesOf(list, 't').map((m) => ({ label: m.label, labelColor: m.labelColor, text: m.text }))

export const tickerBarSaveInputOf = (draft: TickerBarDraft): TickerBarSaveInput => ({
  name: draft.name.trim(),
  topMessages: inputMessagesOf(draft.topMessages),
  bottomMessages: inputMessagesOf(draft.bottomMessages),
  style: {
    ...draft.style,
    top: { ...draft.style.top },
    bottom: { ...draft.style.bottom }
  }
})

export const tickerBarPreviewOf = (draft: TickerBarDraft): TickerBarMarqueeInput => ({
  style: draft.style,
  topMessages: draft.style.showTopBar ? normalizedTickerBarMessagesOf(draft.topMessages, 't') : [],
  bottomMessages: normalizedTickerBarMessagesOf(draft.bottomMessages, 'b')
})

export const tickerBarPreviewOfRow = (row: TickerBar): TickerBarMarqueeInput => {
  const style = resolveTickerBarStyle(row.style)
  return {
    style,
    topMessages: style.showTopBar ? (row.topMessages ?? []) : [],
    bottomMessages: row.bottomMessages ?? []
  }
}

export const tickerBarDraftBytesOf = (draft: TickerBarDraft): number =>
  pushBytesOf({
    topMessages: normalizedTickerBarMessagesOf(draft.topMessages, 't'),
    bottomMessages: normalizedTickerBarMessagesOf(draft.bottomMessages, 'b')
  })

export type TickerBarProblemPlace = 'name' | 'top' | 'bottom' | 'budget'
export type TickerBarProblemField = 'name' | 'count' | 'label' | 'text' | 'labelColor' | 'size'

export interface TickerBarDraftProblem {
  place: TickerBarProblemPlace
  index: number | null
  field: TickerBarProblemField
  message: string
}

export const TICKER_BAR_COPY = {
  nameRule: `Name is required and may be at most ${MAX_TICKER_BAR_NAME} characters`,
  printableName: 'The name may hold printable characters only (no line breaks or control characters)',
  tooManyMessages: `A bar may hold at most ${MAX_TICKER_BAR_MESSAGES} messages`,
  labelRule: `A label may be at most ${MAX_TICKER_BAR_LABEL} characters`,
  textRule: `Message text is required and may be at most ${MAX_TICKER_BAR_TEXT} characters`,
  printable: 'Use printable characters only (no line breaks, control characters or broken characters)',
  labelColorRule: 'Label color must be a color like #33cc66, or empty to use the bar\'s label color',
  tooLong: 'This Message ticker is too long to send live; shorten or remove some messages',
  nearBudget: 'This Message ticker is close to the most it can send live.',
  textTaller: 'This text is taller than the bar\'s row, so it will be cut. Make the row taller.',
  logoShrunk: 'The logo is shrunk to fit the bar. Make the bar taller for a bigger logo.',
  showChangesHelp: 'Saves and restarts both bars now, so the new text shows right away. The bars jump once.',
  emptyBarHint: 'Add a message to see it here',
  emptyList: 'No Message tickers yet.',
  conflict: 'This Message ticker was just saved somewhere else. Reload it and save again.'
} as const

const messageProblems = (list: TickerBarMessageDraft[], place: 'top' | 'bottom'): TickerBarDraftProblem[] => {
  const out: TickerBarDraftProblem[] = []
  if (list.length > MAX_TICKER_BAR_MESSAGES) {
    out.push({ place, index: null, field: 'count', message: TICKER_BAR_COPY.tooManyMessages })
  }
  list.forEach((m, index) => {
    const label = m.label.trim()
    const text = m.text.trim()
    if (label.length > MAX_TICKER_BAR_LABEL) out.push({ place, index, field: 'label', message: TICKER_BAR_COPY.labelRule })
    else if (!hasOnlyPrintableCharacters(label)) out.push({ place, index, field: 'label', message: TICKER_BAR_COPY.printable })
    if (text.length < 1 || text.length > MAX_TICKER_BAR_TEXT) out.push({ place, index, field: 'text', message: TICKER_BAR_COPY.textRule })
    else if (!hasOnlyPrintableCharacters(text)) out.push({ place, index, field: 'text', message: TICKER_BAR_COPY.printable })
    if (m.labelColor != null && m.labelColor !== '' && !isHex6Color(m.labelColor)) {
      out.push({ place, index, field: 'labelColor', message: TICKER_BAR_COPY.labelColorRule })
    }
  })
  return out
}

export const tickerBarDraftProblems = (draft: TickerBarDraft): TickerBarDraftProblem[] => {
  const out: TickerBarDraftProblem[] = []
  const name = draft.name.trim()
  if (name.length < 1 || name.length > MAX_TICKER_BAR_NAME) out.push({ place: 'name', index: null, field: 'name', message: TICKER_BAR_COPY.nameRule })
  else if (!hasOnlyPrintableCharacters(name)) out.push({ place: 'name', index: null, field: 'name', message: TICKER_BAR_COPY.printableName })
  out.push(...messageProblems(draft.topMessages, 'top'), ...messageProblems(draft.bottomMessages, 'bottom'))
  if (tickerBarDraftBytesOf(draft) > TICKER_BAR_PUSH_BUDGET_BYTES) {
    out.push({ place: 'budget', index: null, field: 'size', message: TICKER_BAR_COPY.tooLong })
  }
  return out
}

export const tickerBarNearBudget = (draft: TickerBarDraft): boolean => {
  const bytes = tickerBarDraftBytesOf(draft)
  return bytes > TICKER_BAR_PUSH_BUDGET_BYTES * TICKER_BAR_NEAR_BUDGET_FRACTION && bytes <= TICKER_BAR_PUSH_BUDGET_BYTES
}

export const tickerBarTextTallerThanRow = (bar: TickerBarBarStyle): boolean =>
  Math.max(bar.labelSizePx, bar.textSizePx) > bar.lineHeightPx
